import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import ComplianceFooter from "@/components/ComplianceFooter";

const CookiePolicy = () => {
  useEffect(() => {
    document.title = "Cookie Policy - Ascendry";
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
        <h1 className="text-4xl font-bold mb-8 text-center">Cookie Policy</h1>
        
        <div className="prose prose-lg max-w-none space-y-6">
          <p className="text-muted-foreground">
            <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
          </p>

          <section>
            <h2 className="text-2xl font-semibold mb-4">What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and 
              improving our platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">How We Use Cookies</h2>
            <p>We use cookies for the following purposes:</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2">Essential Cookies</h3>
                <p>These cookies are necessary for the website to function properly:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Authentication cookies:</strong> Keep you signed in to your account</li>
                  <li><strong>Security cookies:</strong> Protect against fraud and ensure secure transactions</li>
                  <li><strong>Preference cookies:</strong> Remember your settings (e.g., sidebar state)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Functional Cookies</h3>
                <p>These cookies enhance your experience:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Session management:</strong> Maintain your session state during visits</li>
                  <li><strong>User interface:</strong> Remember your layout preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2">Third-Party Cookies</h3>
                <p>We use services from trusted partners that may set their own cookies:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Supabase:</strong> Authentication and database services</li>
                  <li><strong>Stripe:</strong> Payment processing and fraud protection</li>
                  <li><strong>Zoom:</strong> Video conferencing integration</li>
                  <li><strong>Google Fonts:</strong> Web font delivery</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Managing Your Cookie Preferences</h2>
            <p>You can control cookies through your browser settings:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Most browsers allow you to view, manage, and delete cookies</li>
              <li>You can set your browser to refuse all cookies or alert you when cookies are being sent</li>
              <li>Please note that disabling essential cookies may affect website functionality</li>
            </ul>
            
            <div className="bg-muted p-4 rounded-lg mt-4">
              <h4 className="font-semibold mb-2">Browser Cookie Settings:</h4>
              <ul className="space-y-1 text-sm">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Cookie Retention</h2>
            <p>Different cookies are stored for different periods:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Authentication cookies:</strong> Stored for up to 30 days or until you log out</li>
              <li><strong>Preference cookies:</strong> Stored for up to 1 year</li>
              <li><strong>Third-party cookies:</strong> Controlled by their respective privacy policies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Any changes will be posted on this page 
              with an updated effective date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p>
              If you have questions about our use of cookies, please contact us at{" "}
              <a href="mailto:lance@blaqout.us" className="text-primary hover:underline">
                lance@blaqout.us
              </a>
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default CookiePolicy;